import React from 'react';
import { Flex, Card, CardContent, Heading, Text, Button, ThemeProvider, Divider, TextField, Spinner } from '@chatsdet/ui';
import { Link } from '@tanstack/react-router';
import { useForgotPassword, EmailAuthError } from '../../hooks/useEmailAuth';
import { BRAND } from '../../brand';
import { CofactorMark } from '../components/CofactorMark';

export function ForgotPasswordPage() {
  const name = BRAND.name;
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  const forgotPasswordMutation = useForgotPassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await forgotPasswordMutation.mutateAsync(email);
      setSuccess(true);
    } catch (err) {
      if (err instanceof EmailAuthError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

        <Card variant="bordered" className="relative max-w-md w-full shadow-2xl">
          <CardContent className="p-8 space-y-8">
            {/* Logo/Brand Section */}
            <div className="text-center space-y-3">
              <div className="flex justify-center mb-2">
                <CofactorMark size={80} className="text-foreground" />
              </div>
              <Heading level={1} className="text-2xl font-bold text-foreground">
                Reset your password
              </Heading>
              <Text className="text-muted-foreground">
                {success
                  ? 'Check your email for reset instructions'
                  : "Enter your email and we'll send you a reset link"}
              </Text>
            </div>

            <Divider />

            {success ? (
              <div className="space-y-6">
                <div className="p-4 bg-success/10 border border-success/20 rounded-md text-center">
                  <Text size="sm" className="text-success">
                    If an account exists for <strong>{email}</strong>, you will
                    receive a password reset email shortly.
                  </Text>
                </div>

                <Text size="sm" className="text-center text-muted-foreground">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    className="text-primary hover:underline"
                  >
                    try again
                  </button>
                </Text>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-primary hover:underline"
                  >
                    Back to sign in
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <Text size="sm" className="text-destructive">
                      {error}
                    </Text>
                  </div>
                )}

                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  autoComplete="email"
                  isRequired
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isDisabled={forgotPasswordMutation.isPending}
                >
                  {forgotPasswordMutation.isPending ? (
                    <Flex align="center" gap="sm">
                      <Spinner size="sm" />
                      <span>Sending...</span>
                    </Flex>
                  ) : (
                    'Send reset link'
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-primary hover:underline"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center">
          <Text size="xs" className="text-muted-foreground">
            © {new Date().getFullYear()} {name}. All rights reserved.
          </Text>
        </div>
      </div>
    </ThemeProvider>
  );
}
